import { useState, useRef, useEffect, useCallback } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

/**
 * MentionInput - An input that supports @mention autocomplete.
 * Mentions are stored as @[Full Name](user-id) in the text.
 */
export default function MentionInput({ value, onChange, onSubmit, users, placeholder, className }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(-1);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const updateMentionState = useCallback((text, cursorPos) => {
    // Look backward from cursor for an unfinished @mention
    const beforeCursor = text.slice(0, cursorPos);
    const atIndex = beforeCursor.lastIndexOf("@");
    if (atIndex === -1) {
      setShowDropdown(false);
      return;
    }
    // Check there's no space before @ (or it's at the start / after space)
    if (atIndex > 0 && beforeCursor[atIndex - 1] !== " " && beforeCursor[atIndex - 1] !== "\n") {
      setShowDropdown(false);
      return;
    }
    // Check if the @ is already inside a completed mention @[...](...) 
    const afterAt = beforeCursor.slice(atIndex);
    if (/^@\[[^\]]*\]\([^)]*\)/.test(afterAt)) {
      setShowDropdown(false);
      return;
    }
    const query = beforeCursor.slice(atIndex + 1);
    // If the query contains a closing bracket, it's already a mention
    if (query.includes("]") || query.includes("(")) {
      setShowDropdown(false);
      return;
    }
    setMentionStart(atIndex);
    setMentionQuery(query);
    const filtered = users.filter((u) =>
      u.full_name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredUsers(filtered);
    setSelectedIndex(0);
    setShowDropdown(filtered.length > 0);
  }, [users]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    updateMentionState(newValue, e.target.selectionStart || newValue.length);
  };

  const insertMention = (user) => {
    const before = value.slice(0, mentionStart);
    const after = value.slice(inputRef.current?.selectionStart || value.length);
    const mention = `@[${user.full_name}](${user.id}) `;
    const newValue = before + mention + after;
    onChange(newValue);
    setShowDropdown(false);
    setMentionQuery("");
    // Focus back on input
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = before.length + mention.length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (showDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredUsers.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        if (filteredUsers[selectedIndex]) {
          e.preventDefault();
          insertMention(filteredUsers[selectedIndex]);
          return;
        }
      } else if (e.key === "Escape") {
        setShowDropdown(false);
        return;
      }
    }
    if (e.key === "Enter" && !showDropdown && onSubmit) {
      onSubmit();
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get display text for the input (show raw text with mention markup)
  const displayValue = value;

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        data-testid="comment-input"
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || "Write a comment... Use @ to mention"}
        className={className || "w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all"}
      />
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-sm shadow-lg z-50 max-h-48 overflow-y-auto"
          data-testid="mention-dropdown"
        >
          {filteredUsers.map((user, index) => (
            <button
              key={user.id}
              data-testid={`mention-option-${user.id}`}
              onClick={() => insertMention(user)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                index === selectedIndex ? "bg-gray-100" : "hover:bg-gray-50"
              }`}
            >
              <Avatar className="h-6 w-6 border border-gray-200">
                <AvatarImage src={user.avatar_url} alt={user.full_name} />
                <AvatarFallback className="text-[10px]">
                  {user.full_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-black">{user.full_name}</span>
                {user.assignment_focus?.length > 0 && (
                  <span className="text-[10px] text-gray-400">
                    {user.assignment_focus.join(", ")}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
